import type {
  AddMembersResult,
  AudienceInput,
  CreateGroupRequest,
  GroupDeletionPreview,
  GroupDeletionResult,
  GroupDetail,
  GroupList,
  GroupMembersPage,
  OwnerRef,
  PeopleSearchResult,
  ResolvePeopleResult,
  UpdateGroupRequest
} from '@ralph/contracts'
import { api, apiGet, apiSend } from './client.ts'

/**
 * Groups (PRD-26): objects this platform owns — created by administrators, their members added
 * and removed by the managers administrators appoint.
 *
 * Everyone may read a group's name, icon, size and managers; its members and what it gives access
 * to go to insiders only, and the server decides who that is. Nothing here guesses.
 */

const groupPath = (id: string): string => `/api/groups/${encodeURIComponent(id)}`

export type GroupListQuery = { q?: string; scope?: 'all' | 'mine'; limit?: number }

export const fetchGroups = (query: GroupListQuery = {}): Promise<GroupList> => {
  const params = new URLSearchParams()
  if (query.q && query.q.trim() !== '') params.set('q', query.q.trim())
  if (query.scope) params.set('scope', query.scope)
  if (query.limit) params.set('limit', String(query.limit))
  const suffix = params.toString()
  return apiGet<GroupList>(`/api/groups${suffix === '' ? '' : `?${suffix}`}`)
}

export const fetchGroup = (id: string): Promise<GroupDetail> => apiGet<GroupDetail>(groupPath(id))

export const fetchGroupMembers = (id: string, options: { q?: string; cursor?: string | null } = {}) => {
  const params = new URLSearchParams()
  if (options.q && options.q.trim() !== '') params.set('q', options.q.trim())
  if (options.cursor) params.set('cursor', options.cursor)
  const suffix = params.toString()
  return apiGet<GroupMembersPage>(`${groupPath(id)}/members${suffix === '' ? '' : `?${suffix}`}`)
}

export const createGroup = (input: Partial<CreateGroupRequest> & { name: string }): Promise<GroupDetail> =>
  apiSend<GroupDetail>('/api/groups', 'POST', { managerIds: [], ...input })

/** Rename or restyle, bound to the version the administrator read (GRP-002). */
export const updateGroup = (id: string, patch: UpdateGroupRequest, version: number): Promise<GroupDetail> =>
  apiSend<GroupDetail>(groupPath(id), 'PATCH', patch, { 'if-match': String(version) })

export const addGroupMembers = (id: string, userIds: string[]): Promise<AddMembersResult> =>
  apiSend<AddMembersResult>(`${groupPath(id)}/members`, 'POST', { userIds })

export const removeGroupMember = (id: string, userId: string): Promise<{ removed: boolean }> =>
  apiSend<{ removed: boolean }>(`${groupPath(id)}/members/${encodeURIComponent(userId)}`, 'DELETE')

export const appointGroupManager = (id: string, userId: string): Promise<{ appointed: boolean }> =>
  apiSend<{ appointed: boolean }>(`${groupPath(id)}/managers`, 'POST', { userId })

export const removeGroupManager = (id: string, userId: string): Promise<{ removed: boolean }> =>
  apiSend<{ removed: boolean }>(`${groupPath(id)}/managers/${encodeURIComponent(userId)}`, 'DELETE')

export const fetchGroupDeletionPreview = (id: string): Promise<GroupDeletionPreview> =>
  apiGet<GroupDeletionPreview>(`${groupPath(id)}/deletion-preview`)

export const deleteGroup = (
  id: string,
  input: { confirmName: string; digest: string; newOwner?: OwnerRef }
): Promise<GroupDeletionResult> =>
  api<GroupDeletionResult>(groupPath(id), { method: 'DELETE', body: JSON.stringify(input) })

// ---------------------------------------------------------------------------
// Reach: how many people an audience would reach (GRP-011)
// ---------------------------------------------------------------------------

export type ReachItem = { kind: 'agent'; id: string } | { kind: 'knowledge_base'; id: string }

/** `people` for the audience being chosen; `current` for what is live now, when an item is named. */
export type Reach = { people: number; current: number | null }

export const fetchReach = (audience: AudienceInput, item?: ReachItem): Promise<Reach> =>
  apiSend<Reach>('/api/groups/reach', 'POST', { audience, ...(item ? { item } : {}) })

// ---------------------------------------------------------------------------
// Finding people (D-8): managers and administrators only
// ---------------------------------------------------------------------------

export const searchPeople = (q: string): Promise<PeopleSearchResult> =>
  apiGet<PeopleSearchResult>(`/api/people/search?q=${encodeURIComponent(q.trim())}`)

export const resolvePeople = (emails: string[]): Promise<ResolvePeopleResult> =>
  apiSend<ResolvePeopleResult>('/api/people/resolve', 'POST', { emails })
